import React from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { perkApi } from 'components/loyalty/PerkApi';
import { Perk } from 'components/loyalty/models/PerkModel';
import { LegacyForm } from 'lib/LegacyForm';
import { Option } from 'lib/types';
import { AutoForm } from 'ui/form/AutoForm';
import {
    createModuleForm,
    transformFormFieldToModuleFields,
    transformModuleToFormFields
} from '../forms/createModuleForm';
import { ContentModule, ContentModuleType, EHeightOption } from '../models/ContentModule';
import { Row } from 'ui/Flex';
import { NEW_MODULE_ID } from 'store/content/contentActions';
import { RowCopy } from 'lib/RowCopy';

interface ModuleFormProps {
    module: ContentModule;
    moduleId: string;
    handleSubmit: () => void;
    updateCurrentModuleProperties: (fieldName: string, value: any, id: string) => void;
    onDelete: (id: string) => void;
}

const moduleTypeToTitleMap: Record<ContentModuleType, string> = {
    [ContentModuleType.PRIMARY_ACTION]: 'Primary Action',
    [ContentModuleType.MARKETING]: 'Marketing',
    [ContentModuleType.LOCATIONS]: 'Locations',
    [ContentModuleType.FAVOURITES]: 'Favourites',
    [ContentModuleType.AWARDS]: 'Awards',
    [ContentModuleType.LOYALTY_BALANCE]: 'Loyalty Balance',
    [ContentModuleType.SECRET_DJ]: 'Secret DJ'
};

export const ModuleForm: React.FunctionComponent<ModuleFormProps> = ({
    module,
    moduleId,
    onDelete,
    handleSubmit,
    updateCurrentModuleProperties
}) => {
    const [display, setDisplay] = React.useState(module.display);
    const [formData, setFormData] = React.useState(null);
    const [perkOptions, setPerkOptions] = React.useState<Option[]>([]);
    const [perksLoaded, setPerksLoaded] = React.useState(module.type !== ContentModuleType.AWARDS);

    // Load perks for Awards module
    React.useEffect(() => {
        if (module.type === ContentModuleType.AWARDS) {
            perkApi
                .getAllPointPerks()
                .then(response => {
                    if (response.ok && response.body?.items) {
                        const options: Option[] = response.body.items.map((perk: Perk) => ({
                            value: perk._id,
                            label: perk.internalTitle || perk.title
                        }));
                        setPerkOptions(options);
                    }
                })
                .finally(() => {
                    setPerksLoaded(true);
                });
        }
    }, [module.type]);

    const isReady = !!createModuleForm(module.type) && perksLoaded;
    const transformedFields = transformModuleToFormFields(module, { display, perkOptions });
    const additionalData = {
        display,
        perkOptions,
        // Pass current selections to disable the other field (include/exclude are mutually exclusive)
        selectedIncludePerkIds: formData?.includePerkIds ?? transformedFields?.includePerkIds,
        selectedExcludePerkIds: formData?.excludePerkIds ?? transformedFields?.excludePerkIds
    };
    const form = new LegacyForm(createModuleForm(module.type, additionalData), handleSubmit).update({
        ...transformedFields
    });

    const handleBlur = React.useCallback(
        (name: string, value: any) => {
            setFormData({ ...formData, [name]: value });
            if (name === 'display') {
                setDisplay({ height: value });
            }
            const transformedFields = transformFormFieldToModuleFields(
                module,
                name,
                value,
                form.getFormData()
            );
            transformedFields.forEach(({ name, value }) =>
                updateCurrentModuleProperties(name, value, moduleId)
            );
        },
        [form, formData, module, moduleId, updateCurrentModuleProperties]
    );

    React.useEffect(() => {
        setDisplay(module.display);
    }, [module.display]);

    const handleDelete = React.useCallback(() => {
        onDelete(moduleId);
    }, [onDelete, moduleId]);

    const buttonDisabled = React.useMemo(() => {
        const formData = form.getFormData();
        if (module.type === ContentModuleType.MARKETING && !!display) {
            if (display.height === EHeightOption.LARGE_SQUARE) {
                return formData.items?.length < 2;
            }

            if (display?.height === EHeightOption.SMALL_SQUARE) {
                return formData.items?.length < 3;
            }
        }
    }, [display, form, module.type]);

    const renderButtons = React.useCallback(
        (submitButton: JSX.Element) => (
            <Row align="space-between">
                <Row>
                    <Button variant="outlined" color="error" onClick={handleDelete}>
                        Delete Module
                    </Button>
                </Row>
                {submitButton}
            </Row>
        ),
        [handleDelete]
    );

    if (!isReady) {
        return (
            <Box padding={2} display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box padding={2}>
            <AutoForm
                buttonDisabled={buttonDisabled}
                onBlur={handleBlur}
                form={form}
                submitLabel="Save"
                renderButtons={renderButtons}
                title={`Configure your ${moduleTypeToTitleMap[module.type]} module`}
            >
                {(fields, render) => (
                    <>
                        {!!module.id && module.id !== NEW_MODULE_ID && (
                            <Grid container spacing={2} ml={0}>
                                <Grid item xl={12} xs={12}>
                                    <RowCopy value={module.id} text="ID" variant="subtitle2" />
                                </Grid>
                            </Grid>
                        )}
                        {render(fields)}
                    </>
                )}
            </AutoForm>
        </Box>
    );
};
